import { ref, shallowRef, toRaw, nextTick, watch, onUnmounted } from 'vue';
import * as _ from 'lodash';
import {
  SlickGrid,
  SlickRowSelectionModel,
  SlickAutoTooltips,
  type GridOption,
  type Column,
  type SlickPlugin,
  type OnSelectedRowsChangedEventArgs,
  SlickDataView,
} from 'slickgrid';
import { LocalData } from './LocalData';
import {
  DataExport,
  HeaderRowFilters,
  AutosizeColumn,
  RowTotalsPlugin,
  State,
  CheckboxColumn,
  CommandColumn,
} from './plugins';
import {
  applyUserVisibleSelectionDiff,
  fixColumnHeaderClickedAfterResize,
  getCssClasses,
  isPluginObject,
  pruneMissingIds,
  recomputeVisibleSelectionFromDataView,
} from './helpers';
import { RemoteDataWrapper } from './RemoteDataWrapper';
import { configure as configureColumn } from './columns';
import { getAllFormatters } from './formatters';

export interface UseSlickGridOptions {
  props: any;
  emit: any;
}

export function useSlickGrid({ props, emit }: UseSlickGridOptions) {
  const slickGridInstance = shallowRef<SlickGrid<any> | null>(null);
  const localDataInstance = shallowRef<
    LocalData<any> | RemoteDataWrapper | null
  >(null);

  const isLoading = ref(false);
  const totalRowCount = ref(0);
  const visibleRowCount = ref(0);
  const isFilteredView = ref(false);
  const requestsCompleted = ref(0);

  const fullSelectedIds = ref<any[]>([]);
  const visibleSelectedIds = ref<any[]>([]);
  let suppressInternalSelectionEvent = false;

  const autosizeApplied = ref(false);
  const dataExportPlugin = shallowRef<DataExport<any> | null>(null);
  const commandColumnPlugins = shallowRef<CommandColumn<any>[]>([]);
  const checkboxSelectorPlugin = shallowRef<CheckboxColumn<any> | null>(null);
  const statePlugin = shallowRef<State<any> | null>(null);

  const getSelectedItems = (grid: SlickGrid<any> | null): any[] => {
    if (!grid || !localDataInstance.value) {
      return [];
    }
    const selectedRowIndexes = grid.getSelectedRows();
    return selectedRowIndexes
      .map((i) => localDataInstance.value!.data.getItem(i))
      .filter((item) => item != null);
  };

  const getAllSelectedItems = (): any[] => {
    if (!localDataInstance.value) {
      return [];
    }
    const dataView = localDataInstance.value.data as SlickDataView<any>;
    if (typeof (dataView.getItemById) !== 'function') {
      return [];
    }
    return fullSelectedIds.value
      .map((id) => dataView.getItemById(id))
      .filter((item: any) => item != null);
  };

  const recomputeVisibleSelection = (
    grid: SlickGrid<any> | null,
  ): { indexes: number[]; ids: any[] } => {
    if (!grid || !localDataInstance.value) {
      return { indexes: [], ids: [] };
    }
    return recomputeVisibleSelectionFromDataView(
      localDataInstance.value.data as SlickDataView<any>,
      fullSelectedIds.value,
    );
  };

  const emitSelectionChanged = (
    grid: SlickGrid<any>,
    args: { rows: number[]; [key: string]: any },
  ) => {
    emit('selectedRowsChanged', {
      sender: grid,
      ...args,
      selectedIds: [...visibleSelectedIds.value],
      allSelectedIds: [...fullSelectedIds.value],
    });
  };

  const clearSelection = () => {
    fullSelectedIds.value = [];
    visibleSelectedIds.value = [];
    if (slickGridInstance.value) {
      suppressInternalSelectionEvent = true;
      try {
        slickGridInstance.value.setSelectedRows([]);
      } finally {
        suppressInternalSelectionEvent = false;
      }
      emitSelectionChanged(slickGridInstance.value, { rows: [] });
    }
  };

  const updateSelectedRows = (changes: Record<string, any>) => {
    if (!localDataInstance.value || visibleSelectedIds.value.length === 0) {
      return;
    }

    if (localDataInstance.value instanceof LocalData) {
      const dataLayer = localDataInstance.value;
      const dataView = dataLayer.data as SlickDataView<any>;

      const idsToUpdate = [...visibleSelectedIds.value];

      if (slickGridInstance.value) {
        slickGridInstance.value.setSelectedRows([]);
      }

      dataView.beginUpdate();

      idsToUpdate.forEach((id) => {
        const item = dataView.getItemById(id);
        if (item) {
          const updatedItem = { ...item, ...changes };
          dataLayer.updateItem(id, updatedItem);
        }
      });

      dataView.endUpdate();
    } else {
      console.warn('RemoteData update not implemented on client side yet.');
    }
  };

  const dataRowAdapter = (dataItem: any) => {
    const itemMetadata: any = {};
    if (props.rowClass && typeof props.rowClass === 'function') {
      const rowCssClass = props.rowClass(dataItem);
      if (rowCssClass) {
        dataItem['$slickRowCssClass'] = rowCssClass;
        itemMetadata['cssClasses'] = getCssClasses(rowCssClass);
      }
    }

    if (dataItem.id != null && fullSelectedIds.value.includes(dataItem.id)) {
      dataItem['$selected'] = true;
    } else {
      dataItem['$selected'] = false;
    }

    dataItem['$__itemMetadata__'] = !_.isEmpty(itemMetadata)
      ? itemMetadata
      : null;
    return dataItem;
  };

  const getColumnsWithSystem = (baseColumns: Column<any>[]): Column<any>[] => {
    const cols = _.cloneDeep(baseColumns).map((col) =>
      configureColumn(col, props.columnDefaults),
    );

    if (props.checkboxRowSelection) {
      if (!checkboxSelectorPlugin.value) {
        checkboxSelectorPlugin.value = new CheckboxColumn({
          headerSelect: true,
          rowSelector: true,
          cssClass: 'slick-cell-checkboxsel',
        });
      }
      cols.unshift(checkboxSelectorPlugin.value as unknown as Column<any>);
    }

    if (props.rowHeader) {
      cols.unshift({
        id: '_rowHeader',
        name: '',
        field: 'sel',
        width: 40,
        resizable: false,
        unselectable: true,
        focusable: false,
        cssClass: 'slick-cell-rowHeader',
      });
    }

    return cols;
  };

  const getPlugins = (
    dataLayer: LocalData<any> | RemoteDataWrapper,
    columns: Column<any>[],
  ): SlickPlugin[] => {
    const plugins: SlickPlugin[] = [];

    _.each(columns, (col: any) => {
      if (isPluginObject(col)) {
        plugins.push(col);

        if (col.pluginName === 'CommandColumn') {
          commandColumnPlugins.value = [
            ...commandColumnPlugins.value,
            col as CommandColumn<any>,
          ];
        }
      }
    });

    plugins.push(dataLayer);

    if (props.headerRowFilter) {
      const filterPlugin = new HeaderRowFilters();
      filterPlugin.onColumnFilterChange.subscribe((_e: any, args: any) => {
        if (dataLayer instanceof RemoteDataWrapper) {
          (dataLayer as RemoteDataWrapper).setFilter(args.filters);
          return;
        }

        const grid = slickGridInstance.value;
        if (!grid) {
          (dataLayer as LocalData<any>).filter(args.filters);
          return;
        }

        suppressInternalSelectionEvent = true;
        try {
          (dataLayer as LocalData<any>).filter(args.filters);
          const { indexes, ids } = recomputeVisibleSelection(grid);
          grid.setSelectedRows(indexes);
          visibleSelectedIds.value = ids;
        } finally {
          suppressInternalSelectionEvent = false;
        }

        emitSelectionChanged(grid, { rows: grid.getSelectedRows() });
      });
      plugins.push(filterPlugin);
    }

    if (!props.noTooltips) {
      plugins.push(
        new SlickAutoTooltips({
          enableForCells: props.tooltipsEnableForCells,
          enableForHeaderCells: props.tooltipsEnableForHeaderCells,
          maxToolTipLength: props.tooltipsMaxToolTipLength,
        }),
      );
    }

    const exporter = new DataExport({
      filename: props.exportToCsvFilename,
      quoteChar: props.exportToCsvQuotechar,
      delimiter: props.exportToCsvDelimiter,
      getDataItems: (visibleOnly) => dataLayer.getItems(visibleOnly),
      localeCode: props.localeCode || 'en',
    });
    dataExportPlugin.value = exporter;
    plugins.push(exporter);

    plugins.push(new AutosizeColumn());

    if (props.footerTotals) {
      plugins.push(new RowTotalsPlugin());
    }

    const actualStateKey = props.stateKey || `slickg_state_${props.name}`;

    if (actualStateKey) {
      const state = new State({
        key: actualStateKey,
        defaultColumns: toRaw(columns),
      });
      statePlugin.value = state;
      plugins.push(state);
    }

    return plugins;
  };

  const bindEvents = (grid: SlickGrid<any>, dataLayer: any) => {
    dataLayer.onDataLoadFailed.subscribe((_e: any, args: any) => {
      isLoading.value = false;
      requestsCompleted.value++;
      emit('error', { type: 'dataload', error: args.error });
    });

    dataLayer.onDataLoading.subscribe(() => {
      isLoading.value = true;
    });

    dataLayer.onDataLoaded.subscribe((_e: any, args: any) => {
      isLoading.value = false;
      requestsCompleted.value++;

      let items;
      if (dataLayer instanceof RemoteDataWrapper) {
        items = args.items;
        totalRowCount.value = args.total || 0;
        visibleRowCount.value = items.length;
      } else {
        items = dataLayer.getItems();
      }

      if (dataLayer instanceof LocalData) {
        fullSelectedIds.value = pruneMissingIds(
          fullSelectedIds.value,
          (dataLayer as LocalData<any>).getItems(false),
        );
      }

      if (grid.getSelectionModel()) {
        suppressInternalSelectionEvent = true;
        try {
          const { indexes, ids } = recomputeVisibleSelection(grid);
          grid.setSelectedRows(indexes);
          visibleSelectedIds.value = ids;
        } finally {
          suppressInternalSelectionEvent = false;
        }
        emitSelectionChanged(grid, { rows: grid.getSelectedRows() });
      }

      if (!autosizeApplied.value && items && items.length > 0) {
        const autosizePlugin = grid.getPluginByName('AutosizeColumn') as any;
        const hasSavedState = statePlugin.value && !statePlugin.value.isEmpty;

        if (
          !hasSavedState &&
          autosizePlugin &&
          typeof autosizePlugin.resizeAllColumns === 'function'
        ) {
          autosizeApplied.value = true;
          autosizePlugin.resizeAllColumns();
        }
      }

      emit('dataLoaded', { items });
    });

    dataLayer.onRowCountChanged.subscribe((_item, args) => {
      totalRowCount.value = args.total || 0;
      visibleRowCount.value = args.visible || 0;
      isFilteredView.value = visibleRowCount.value < totalRowCount.value;
    });

    dataLayer.onRowsChanged.subscribe((_item, args) => {
      emit('rowsChanged', args);
      grid.invalidate();
      grid.render();
    });

    grid.onSelectedRowsChanged.subscribe(
      (_e: any, args: OnSelectedRowsChangedEventArgs) => {
        if (suppressInternalSelectionEvent) {
          return;
        }

        const newVisibleIds = args.rows
          .map((i) => grid.getDataItem(i))
          .filter((item) => item != null)
          .map((item) => item.id);

        fullSelectedIds.value = applyUserVisibleSelectionDiff(
          newVisibleIds,
          visibleSelectedIds.value,
          fullSelectedIds.value,
        );
        visibleSelectedIds.value = newVisibleIds;

        emit('selectedRowsChanged', {
          sender: grid,
          ...args,
          selectedIds: newVisibleIds,
          allSelectedIds: [...fullSelectedIds.value],
        });
      },
    );

    grid.onSort.subscribe((_item, args) => emit('sort', args));
    grid.onScroll.subscribe((_item, args) => emit('scroll', args));
    grid.onClick.subscribe((item, args) => emit('click', { e: item, args }));
    grid.onDblClick.subscribe((item, args) =>
      emit('dblClick', { e: item, args }),
    );

    for (const cmdColPlugin of commandColumnPlugins.value) {
      cmdColPlugin.onCommand.subscribe((_e: any, args: any) => {
        emit('command', args);
      });
    }
  };

  const initGrid = (containerEl: HTMLElement) => {
    if (!containerEl) {
      return;
    }

    let dataLayer;

    if (props.backendUrl) {
      dataLayer = new RemoteDataWrapper({
        url: props.backendUrl,
      });
    } else {
      dataLayer = new LocalData({
        items: toRaw(props.items),
        rowAdapter: dataRowAdapter,
        groupBy: props.grouping != null,
      });
    }

    localDataInstance.value = dataLayer as any;

    const finalColumns = getColumnsWithSystem(toRaw(props.columns));
    const plugins = getPlugins(dataLayer, finalColumns);

    const gridOptions: GridOption = {
      ...toRaw(props.options),
      explicitInitialization: true,
      enableCellNavigation: true,
      enableColumnReorder: !props.noColumnReorder,
      editCommandHandler: props.editCommandHandler,
      enableSanitizeHtml: false,
      enableHtmlRendering: true,
    };

    if (props.footerTotals) {
      gridOptions.createFooterRow = true;
      gridOptions.showFooterRow = true;
      gridOptions.footerRowHeight = 25;
    }

    const allPlugins = [...plugins];
    if (checkboxSelectorPlugin.value) {
      allPlugins.push(checkboxSelectorPlugin.value);
    }

    allPlugins.forEach((plugin) => {
      const fn = (plugin as any).getOptions;
      if (typeof fn === 'function') {
        _.extend(gridOptions, fn());
      }
    });

    const grid = new SlickGrid(
      containerEl,
      dataLayer.data,
      finalColumns,
      gridOptions,
    );
    slickGridInstance.value = grid;
    (grid as any).ALL_FORMATTERS = getAllFormatters();
    (grid as any).getSelectedItems = () => getSelectedItems(grid);
    (grid as any).getAllSelectedItems = getAllSelectedItems;

    if (checkboxSelectorPlugin.value) {
      grid.registerPlugin(checkboxSelectorPlugin.value);
    }

    if (props.checkboxRowSelection && checkboxSelectorPlugin.value) {
      grid.setSelectionModel(
        new SlickRowSelectionModel({
          selectActiveRow: false,
          rowSelector: checkboxSelectorPlugin.value,
        } as any),
      );
    } else if (props.selectionModel) {
      grid.setSelectionModel(toRaw(props.selectionModel));
    } else {
      grid.setSelectionModel(
        new SlickRowSelectionModel({ selectActiveRow: false }),
      );
    }

    plugins.forEach((plugin) => grid.registerPlugin(plugin));

    bindEvents(grid, dataLayer);

    grid.init();

    grid.invalidate();
    grid.resizeCanvas();

    if (grid.onViewportChanged) {
      grid.onViewportChanged.notify({ grid });
    }

    if (props.backendUrl) {
      (dataLayer as RemoteDataWrapper).reload();
    } else if (props.grouping) {
      (dataLayer as LocalData<any>).groupBy(toRaw(props.grouping));
      grid.invalidate();
      grid.resizeCanvas();
    }

    nextTick(() => {
      fixColumnHeaderClickedAfterResize(grid);
    });
  };

  const destroyGrid = () => {
    if (slickGridInstance.value) {
      slickGridInstance.value.destroy();
      slickGridInstance.value = null;
    }
  };

  const triggerExport = async (format: string = 'csv') => {
    if (!dataExportPlugin.value) {
      console.warn('DataExport plugin not available.');
      return false;
    }
    try {
      await dataExportPlugin.value.exportData(format);
      return true;
    } catch (error) {
      console.error('Export failed in composable:', error);
      throw error;
    }
  };

  watch(
    () => props.items,
    (newDataset) => {
      if (
        localDataInstance.value &&
        localDataInstance.value instanceof LocalData
      ) {
        localDataInstance.value.replaceDataSource(toRaw(newDataset));
      }
    },
    { deep: false },
  );

  watch(
    () => props.columns,
    (newColumns) => {
      const cols = getColumnsWithSystem(toRaw(newColumns));
      slickGridInstance.value?.setColumns(cols);
    },
  );

  watch(
    () => props.grouping,
    (newGrouping) => {
      if (
        localDataInstance.value &&
        localDataInstance.value instanceof LocalData
      ) {
        localDataInstance.value.groupBy(
          newGrouping ? toRaw(newGrouping) : ([] as any),
        );
        slickGridInstance.value?.invalidate();
        slickGridInstance.value?.resizeCanvas();
      }
    },
    { deep: true },
  );

  onUnmounted(() => {
    destroyGrid();
  });

  return {
    slickGridInstance,
    localDataInstance,
    initGrid,
    destroyGrid,
    isLoading,
    totalRowCount,
    visibleRowCount,
    isFilteredView,
    selectedIds: visibleSelectedIds,
    allSelectedIds: fullSelectedIds,
    getSelectedItems: () => getSelectedItems(slickGridInstance.value),
    getAllSelectedItems,
    clearSelection,
    exportData: triggerExport,
    updateSelectedRows,
  };
}
